/**
 * Freehand (ink) drawing geometry. A freehand stroke is captured as a polyline
 * and stored as an `add-shape` Edit with `shapeType: 'freehand'`, an open
 * (un-closed) path, and a stroke-only paint — the driver replays the path
 * verbatim, so the saved bytes match and the original prefix is preserved.
 *
 * Because there is no generator for a freehand shape, move / scale translate or
 * scale the stored path points directly (see editor-store).
 */
import type { ShapePathSegment } from '@pdf-ticket/pdf-core';

export interface Pt {
  readonly x: number;
  readonly y: number;
}

export const FREEHAND_SHAPE_TYPE = 'freehand';

/** Drop points closer than `minDist` to the last kept point (cheap smoothing). */
export function decimate(points: readonly Pt[], minDist = 2): Pt[] {
  if (points.length === 0) return [];
  const out: Pt[] = [points[0]!];
  for (let i = 1; i < points.length; i++) {
    const p = points[i]!;
    const last = out[out.length - 1]!;
    if (Math.hypot(p.x - last.x, p.y - last.y) >= minDist) out.push(p);
  }
  // Always keep the final point so the stroke ends where the pointer lifted.
  const final = points[points.length - 1]!;
  const tail = out[out.length - 1]!;
  if (tail.x !== final.x || tail.y !== final.y) out.push(final);
  return out;
}

/** Open polyline path (move + lines) through the points (absolute PDF coords). */
export function freehandPath(points: readonly Pt[]): ShapePathSegment[] {
  if (points.length === 0) return [];
  const [first, ...rest] = points;
  return [
    { type: 'move', x: first!.x, y: first!.y },
    ...rest.map((p): ShapePathSegment => ({ type: 'line', x: p.x, y: p.y })),
  ];
}

/**
 * Smooth polyline → cubic Béziers via Catmull-Rom (the curve passes through every
 * input point; tangents from neighbours, tension 1/6). Falls back to a straight
 * polyline for < 3 points. Result interops with translate/scale/SVG (cubic-aware).
 */
export function smoothFreehandPath(points: readonly Pt[]): ShapePathSegment[] {
  const n = points.length;
  if (n < 3) return freehandPath(points);
  const out: ShapePathSegment[] = [{ type: 'move', x: points[0]!.x, y: points[0]!.y }];
  for (let i = 0; i < n - 1; i++) {
    const p0 = points[i - 1] ?? points[i]!;
    const p1 = points[i]!;
    const p2 = points[i + 1]!;
    const p3 = points[i + 2] ?? p2;
    out.push({
      type: 'cubic',
      x1: p1.x + (p2.x - p0.x) / 6,
      y1: p1.y + (p2.y - p0.y) / 6,
      x2: p2.x - (p3.x - p1.x) / 6,
      y2: p2.y - (p3.y - p1.y) / 6,
      x: p2.x,
      y: p2.y,
    });
  }
  return out;
}

/**
 * Build the /InkList strokes for an `add-ink` edit from a single freehand
 * gesture's points (PDF user space). One gesture is one stroke; the outer array
 * leaves room for future multi-stroke ink. Returns [] for an empty gesture.
 */
export function freehandInkStrokes(points: readonly Pt[]): Pt[][] {
  if (points.length === 0) return [];
  return [points.map((p) => ({ x: p.x, y: p.y }))];
}

/** SVG polyline `points` attribute for an ink stroke, PDF Y-up → SVG Y-down. */
export function inkStrokeSvgPoints(
  stroke: readonly Pt[],
  bounds: { x: number; y: number; width: number; height: number },
  size = 100,
): string {
  return stroke
    .map((p) => {
      const sx = ((p.x - bounds.x) / bounds.width) * size;
      const sy = (1 - (p.y - bounds.y) / bounds.height) * size;
      return `${sx.toFixed(2)},${sy.toFixed(2)}`;
    })
    .join(' ');
}

/** Bounding box of the points (PDF space, never zero-size). */
export function pointsBounds(points: readonly Pt[]): {
  x: number;
  y: number;
  width: number;
  height: number;
} {
  if (points.length === 0) return { x: 0, y: 0, width: 1, height: 1 };
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return { x: minX, y: minY, width: Math.max(1, maxX - minX), height: Math.max(1, maxY - minY) };
}

function mapSeg(
  s: ShapePathSegment,
  f: (x: number, y: number) => { x: number; y: number },
): ShapePathSegment {
  switch (s.type) {
    case 'move': {
      const p = f(s.x, s.y);
      return { type: 'move', x: p.x, y: p.y };
    }
    case 'line': {
      const p = f(s.x, s.y);
      return { type: 'line', x: p.x, y: p.y };
    }
    case 'cubic': {
      const c1 = f(s.x1, s.y1);
      const c2 = f(s.x2, s.y2);
      const p = f(s.x, s.y);
      return { type: 'cubic', x1: c1.x, y1: c1.y, x2: c2.x, y2: c2.y, x: p.x, y: p.y };
    }
    case 'close':
      return s;
  }
}

/** Translate every path point by (dx, dy). */
export function translatePath(
  path: readonly ShapePathSegment[],
  dx: number,
  dy: number,
): ShapePathSegment[] {
  return path.map((s) => mapSeg(s, (x, y) => ({ x: x + dx, y: y + dy })));
}

/**
 * SVG `d` for a freehand path, mapped into a `size`×`size` box normalized to the
 * path's bounds (PDF Y-up → SVG Y-down). Rendered with preserveAspectRatio=none
 * so it stretches to the on-page overlay box exactly like the autoshape paths.
 */
export function freehandSvgD(
  path: readonly ShapePathSegment[],
  bounds: { x: number; y: number; width: number; height: number },
  size = 100,
): string {
  const fx = (x: number): string => (((x - bounds.x) / bounds.width) * size).toFixed(2);
  const fy = (y: number): string => ((1 - (y - bounds.y) / bounds.height) * size).toFixed(2);
  return path
    .map((s) =>
      s.type === 'move'
        ? `M${fx(s.x)} ${fy(s.y)}`
        : s.type === 'line'
          ? `L${fx(s.x)} ${fy(s.y)}`
          : s.type === 'cubic'
            ? `C${fx(s.x1)} ${fy(s.y1)} ${fx(s.x2)} ${fy(s.y2)} ${fx(s.x)} ${fy(s.y)}`
            : 'Z',
    )
    .join(' ');
}

/** Scale every path point about the anchor (ax, ay) by (sx, sy). */
export function scalePath(
  path: readonly ShapePathSegment[],
  ax: number,
  ay: number,
  sx: number,
  sy: number,
): ShapePathSegment[] {
  return path.map((s) => mapSeg(s, (x, y) => ({ x: ax + (x - ax) * sx, y: ay + (y - ay) * sy })));
}

/** Every on-path point of a segment list (incl. cubic controls) for bbox. */
function pathPoints(path: readonly ShapePathSegment[]): Pt[] {
  const pts: Pt[] = [];
  for (const s of path) {
    if (s.type === 'move' || s.type === 'line') pts.push({ x: s.x, y: s.y });
    else if (s.type === 'cubic')
      pts.push({ x: s.x1, y: s.y1 }, { x: s.x2, y: s.y2 }, { x: s.x, y: s.y });
  }
  return pts;
}

/**
 * The anchor (on-curve) points of a path — move/line vertices and cubic
 * end-points, ignoring control points. These are the points a freehand stroke
 * interpolates, so re-smoothing feeds them back through `smoothFreehandPath`.
 */
export function pathAnchorPoints(path: readonly ShapePathSegment[]): Pt[] {
  const pts: Pt[] = [];
  for (const s of path) {
    if (s.type === 'move' || s.type === 'line' || s.type === 'cubic') pts.push({ x: s.x, y: s.y });
  }
  return pts;
}

/** Bounding box of a whole path (including cubic control points). */
export function pathBounds(path: readonly ShapePathSegment[]): {
  x: number;
  y: number;
  width: number;
  height: number;
} {
  return pointsBounds(pathPoints(path));
}

// --- Ink strokes (add-ink: an array of polyline point arrays) ---------------

/** Bounding box across all strokes of an ink annotation. */
export function strokesBounds(strokes: ReadonlyArray<ReadonlyArray<Pt>>): {
  x: number;
  y: number;
  width: number;
  height: number;
} {
  return pointsBounds(strokes.flat());
}

/** Translate every point of every stroke by (dx, dy). */
export function translateStrokes(
  strokes: ReadonlyArray<ReadonlyArray<Pt>>,
  dx: number,
  dy: number,
): Pt[][] {
  return strokes.map((stroke) => stroke.map((p) => ({ x: p.x + dx, y: p.y + dy })));
}

/** Affine-remap all strokes from their current bbox onto `toBounds` (resize). */
export function remapStrokesToBounds(
  strokes: ReadonlyArray<ReadonlyArray<Pt>>,
  toBounds: { x: number; y: number; width: number; height: number },
): Pt[][] {
  const from = strokesBounds(strokes);
  const sx = toBounds.width / from.width;
  const sy = toBounds.height / from.height;
  return strokes.map((stroke) =>
    stroke.map((p) => ({
      x: toBounds.x + (p.x - from.x) * sx,
      y: toBounds.y + (p.y - from.y) * sy,
    })),
  );
}

/**
 * Rotate all stroke points about (cx, cy) by `deg` (CCW in PDF Y-up space,
 * which renders as a clockwise on-screen rotation — matching the add-shape
 * rotation convention and the CSS live-preview). Ink has no rotation field, so
 * the rotation is baked into the points.
 */
export function rotateStrokes(
  strokes: ReadonlyArray<ReadonlyArray<Pt>>,
  cx: number,
  cy: number,
  deg: number,
): Pt[][] {
  const a = (deg * Math.PI) / 180;
  const cos = Math.cos(a);
  const sin = Math.sin(a);
  return strokes.map((stroke) =>
    stroke.map((p) => {
      const ux = p.x - cx;
      const uy = p.y - cy;
      return { x: cx + ux * cos - uy * sin, y: cy + ux * sin + uy * cos };
    }),
  );
}

/**
 * Affine-remap a generator-less path (e.g. freehand) from its current bounding
 * box onto `toBounds`. The stored path IS the geometry for such shapes — moving
 * or resizing the box must move/scale the points, not re-fit them in place
 * (which cancels out and looks like the shape can't move). Pure translate when
 * the box only shifts; scales when it also resizes.
 */
export function remapPathToBounds(
  path: readonly ShapePathSegment[],
  toBounds: { x: number; y: number; width: number; height: number },
): ShapePathSegment[] {
  const from = pointsBounds(pathPoints(path));
  const sx = toBounds.width / from.width;
  const sy = toBounds.height / from.height;
  return path.map((s) =>
    mapSeg(s, (x, y) => ({
      x: toBounds.x + (x - from.x) * sx,
      y: toBounds.y + (y - from.y) * sy,
    })),
  );
}
